/**
 * SOVRANOVA — CaptureView (Ambient Companion, Wave 3: consent-gated capture)
 * ------------------------------------------------------------
 * The live recording screen for an in-session capture (e.g. a therapy session)
 * AFTER every participant has signed and consented in ConsentFlow. Reached only
 * via that flow — there is no route to this screen without a consentId.
 *
 * HOW CAPTURE WORKS — cycle recording, not one long take:
 * MediaRecorder chunks after the first lack container headers, so a single
 * long recording can't be transcribed piecewise. Instead the recorder runs in
 * ~3-minute CYCLES on one microphone stream: each cycle produces a complete,
 * standalone audio file that is transcribed through the existing memory-only
 * /api/voice path (voice discarded, text kept) and persisted as a segment
 * immediately. A dead battery mid-session loses at most the current cycle.
 *
 * WITHDRAWAL: either person taps "Withdraw consent" → recording stops FIRST,
 * then the withdrawing person chooses what happens to the partial transcript.
 * Delete is the default — keeping it requires an explicit choice.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '../../services/api'
import { bufferPut, bufferDelete, clipId } from '../../services/audioBuffer'

const CYCLE_MS = 3 * 60 * 1000  // one complete audio file every ~3 minutes

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

export function CaptureView({ consentId, names = [], onExit }) {
  const [phase, setPhase]       = useState('idle')   // idle → recording → paused → finishing → done | withdraw → withdrawn
  const [elapsed, setElapsed]   = useState(0)
  const [segments, setSegments] = useState(0)
  const [pendingUp, setPendingUp] = useState(0)      // uploads in flight
  const [error, setError]       = useState(null)
  const [result, setResult]     = useState(null)     // reflection after finish
  const [withdrawOutcome, setWithdrawOutcome] = useState(null) // { kept }

  const captureIdRef   = useRef(null)
  const streamRef      = useRef(null)
  const recorderRef    = useRef(null)
  const cycleTimerRef  = useRef(null)
  const tickTimerRef   = useRef(null)
  const runningRef     = useRef(false)   // should a finished cycle start another?
  const carryTextRef   = useRef('')      // transcript that failed to upload; merged into the next segment
  const wakeLockRef    = useRef(null)
  const elapsedRef     = useRef(0)

  useEffect(() => { elapsedRef.current = elapsed }, [elapsed])

  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
        wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null })
      }
    } catch { /* non-fatal */ }
  }, [])
  const releaseWakeLock = useCallback(() => {
    try { wakeLockRef.current?.release?.() } catch { /* noop */ }
    wakeLockRef.current = null
  }, [])

  const stopTimers = () => {
    if (cycleTimerRef.current) { clearTimeout(cycleTimerRef.current); cycleTimerRef.current = null }
    if (tickTimerRef.current)  { clearInterval(tickTimerRef.current); tickTimerRef.current = null }
  }

  const pickMime = () =>
    MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
      : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus'
      : 'audio/mp4'

  // Transcribe one complete cycle file and persist it as a segment. The audio
  // is already in the on-device safety buffer; the buffered copy self-deletes
  // ONLY once this clip's words are confirmed persisted server-side (the
  // 13 Aug rule). Failed uploads carry their text forward AND keep their audio.
  const processCycleBlob = useCallback(async (blob, mimeType, id) => {
    if (!blob || !blob.size || !captureIdRef.current) { if (id) bufferDelete(id); return }
    setPendingUp(n => n + 1)
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader()
        reader.onload = () => res(reader.result.split(',')[1])
        reader.onerror = rej
        reader.readAsDataURL(blob)
      })
      const doTranscribe = () => api.post('/voice/transcribe', { audio: base64, mimeType })
      let text = ''
      try { ({ text } = await doTranscribe()) }
      catch { await new Promise(r => setTimeout(r, 800)); ({ text } = await doTranscribe()) }

      let content = [carryTextRef.current, (text || '').trim()].filter(Boolean).join('\n')
      carryTextRef.current = ''
      if (!content) { if (id) bufferDelete(id); return }   // silence — nothing worth keeping

      try {
        await api.post(`/ambient/capture/${captureIdRef.current}/segment`, { content })
        setSegments(n => n + 1)
        if (id) bufferDelete(id)        // words persisted -> audio has done its job
      } catch (err) {
        // Consent revoked server-side → stop everything; otherwise keep the
        // text (folded into the next segment) AND the audio (device buffer).
        if (String(err?.message || '').toLowerCase().includes('consent')) {
          runningRef.current = false
          try { recorderRef.current?.stop() } catch { /* noop */ }
          setError('Recording stopped — consent is no longer valid.')
          setPhase('withdraw')
        } else {
          carryTextRef.current = content
        }
      }
    } catch { /* transcription failed twice — the audio SURVIVES in the device buffer, recoverable from Reflect */ }
    finally { setPendingUp(n => Math.max(0, n - 1)) }
  }, [])

  const beginCycle = useCallback(() => {
    const stream = streamRef.current
    if (!stream || !runningRef.current) return
    const mimeType = pickMime()
    const recorder = new MediaRecorder(stream, { mimeType })
    const localChunks = []
    recorder.ondataavailable = e => { if (e.data.size > 0) localChunks.push(e.data) }
    recorder.onerror = () => { try { recorder.stop() } catch { /* noop */ } }
    recorder.onstop = () => {
      const blob = new Blob(localChunks, { type: recorder.mimeType })
      const id = clipId()
      // Safety-buffer FIRST: the audio survives crash/reload/battery until its
      // words are confirmed persisted, then the buffered copy self-deletes.
      bufferPut(id, blob, recorder.mimeType, 'capture').finally(() => {
        processCycleBlob(blob, recorder.mimeType, id)  // async — recording continues
      })
      if (runningRef.current) beginCycle()             // straight into the next cycle (~100ms gap)
    }
    recorderRef.current = recorder
    recorder.start(1000)
    if (cycleTimerRef.current) clearTimeout(cycleTimerRef.current)
    cycleTimerRef.current = setTimeout(() => { try { recorder.stop() } catch { /* noop */ } }, CYCLE_MS)
  }, [processCycleBlob])

  const startCapture = async () => {
    setError(null)
    try {
      const { captureId } = await api.post('/ambient/capture/start', { consentId })
      captureIdRef.current = captureId
    } catch (err) {
      setError(err.message || 'Could not start the capture — check the consent and try again.')
      return
    }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      setError(err.name === 'NotAllowedError' ? 'Microphone permission denied.' : 'Could not access the microphone.')
      return
    }
    acquireWakeLock()
    runningRef.current = true
    setPhase('recording')
    setElapsed(0)
    tickTimerRef.current = setInterval(() => setElapsed(e => e + 1), 1000)
    beginCycle()
  }

  // Stop the mic + cycles. The final partial cycle is flushed as a segment.
  const stopRecordingMachinery = useCallback(() => {
    runningRef.current = false
    stopTimers()
    try { recorderRef.current?.stop() } catch { /* noop */ }
    try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch { /* noop */ }
    streamRef.current = null
    releaseWakeLock()
  }, [releaseWakeLock])

  const waitForUploads = async (maxMs = 45000) => {
    const t0 = Date.now()
    // pendingUp lives in state; poll the DOM-free way via a promise loop.
    while (Date.now() - t0 < maxMs) {
      let n
      setPendingUp(v => { n = v; return v })
      if (n === 0) return
      await new Promise(r => setTimeout(r, 400))
    }
  }

  const endAndReflect = async () => {
    if (phase !== 'recording' && phase !== 'paused') return
    setPhase('finishing')
    stopRecordingMachinery()
    await waitForUploads()
    try {
      const r = await api.post(`/ambient/capture/${captureIdRef.current}/finish`, { durationS: elapsedRef.current })
      setResult(r)
      setPhase('done')
    } catch (err) {
      setError(err.message || 'Could not finish the capture. Your segments are saved — try again in a moment.')
      setPhase('paused')
    }
  }

  const openWithdraw = () => {
    stopRecordingMachinery()      // stop FIRST — the person asked for it to stop
    setPhase('withdraw')
  }

  const confirmWithdraw = async (keep) => {
    setPhase('finishing')
    await waitForUploads()
    try {
      const r = await api.post(`/ambient/capture/${captureIdRef.current}/withdraw`, { keep, durationS: elapsedRef.current })
      setWithdrawOutcome({ kept: r?.kept === true })
      setPhase('withdrawn')
    } catch (err) {
      setError(err.message || 'Could not record the withdrawal — please try again.')
      setPhase('withdraw')
    }
  }

  // Backgrounding suspends the mic on mobile — pause cleanly, segments are safe.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden' && runningRef.current) {
        runningRef.current = false
        stopTimers()
        try { recorderRef.current?.stop() } catch { /* noop */ }
        try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch { /* noop */ }
        streamRef.current = null
        setPhase('paused')
      }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      runningRef.current = false
      stopTimers()
      try { recorderRef.current?.stop() } catch { /* noop */ }
      try { streamRef.current?.getTracks().forEach(t => t.stop()) } catch { /* noop */ }
      releaseWakeLock()
    }
  }, [releaseWakeLock])

  const resume = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setError('Could not re-open the microphone.')
      return
    }
    acquireWakeLock()
    runningRef.current = true
    setPhase('recording')
    tickTimerRef.current = setInterval(() => setElapsed(e => e + 1), 1000)
    beginCycle()
  }

  const who = names.filter(Boolean).join(' and ')

  return (
    <div style={c.wrap}>
      <style>{'@keyframes sovcap{0%,100%{opacity:.35;transform:scale(1)}50%{opacity:1;transform:scale(1.25)}}'}</style>

      {phase === 'idle' && (
        <div style={c.card}>
          <div style={c.icon}>🎙</div>
          <h1 style={c.title}>Ready to record</h1>
          <p style={c.sub}>Consent is signed{who ? ` by ${who}` : ''}. When you start, everyone in the room can see this screen is recording, and anyone can stop it at any time.</p>
          <ul style={c.factList}>
            <li style={c.fact}>Voice is transcribed then discarded — only the transcript is kept, private to this account.</li>
            <li style={c.fact}>The transcript saves as it goes, every few minutes.</li>
            <li style={c.fact}>Withdrawing consent stops recording instantly — deletion is the default.</li>
          </ul>
          {error && <p style={c.error}>{error}</p>}
          <div style={c.controls}>
            <button style={c.ghost} onClick={() => onExit?.()}>Back</button>
            <button style={c.primary} onClick={startCapture}>● Start recording</button>
          </div>
        </div>
      )}

      {(phase === 'recording' || phase === 'paused' || phase === 'finishing') && (
        <div style={c.liveWrap}>
          <div style={{ ...c.pulseDot, ...(phase !== 'recording' ? { animation: 'none', opacity: .3 } : {}) }} />
          <p style={c.liveState}>
            {phase === 'recording' ? 'Recording' : phase === 'paused' ? 'Paused' : 'Saving…'}
          </p>
          <p style={c.timer}>{fmt(elapsed)}</p>
          {who && <p style={c.who}>With consent from {who}</p>}
          <p style={c.saved}>{segments} segment{segments === 1 ? '' : 's'} saved{pendingUp > 0 ? ` · ${pendingUp} uploading` : ''}</p>
          <p style={c.liveHint}>The screen stays on and visible — that's the recording indicator everyone agreed to.</p>
          {error && <p style={c.error}>{error}</p>}
          <div style={c.liveControls}>
            {phase === 'paused' && <button style={c.primary} onClick={resume}>● Resume</button>}
            {phase !== 'finishing' && <button style={c.stopBig} onClick={endAndReflect}>■ End &amp; reflect</button>}
            {phase !== 'finishing' && <button style={c.withdrawLink} onClick={openWithdraw}>Withdraw consent</button>}
            {phase === 'finishing' && <p style={c.liveHint}>Finishing up — reflecting on the session…</p>}
          </div>
        </div>
      )}

      {phase === 'withdraw' && (
        <div style={c.card}>
          <div style={c.icon}>✋</div>
          <h1 style={c.title}>Recording stopped</h1>
          <p style={c.sub}>Consent is being withdrawn. What should happen to what was captured so far? This choice belongs to the person withdrawing.</p>
          {error && <p style={c.error}>{error}</p>}
          <div style={c.withdrawChoices}>
            <button style={c.primary} onClick={() => confirmWithdraw(false)}>Delete it — remove everything captured</button>
            <button style={c.ghost} onClick={() => confirmWithdraw(true)}>Keep the partial transcript for the account holder</button>
          </div>
          <p style={c.smallNote}>Deleting is the default and removes every saved segment of this session.</p>
        </div>
      )}

      {phase === 'withdrawn' && (
        <div style={c.card}>
          <p style={c.doneMark}>✓</p>
          <h1 style={c.title}>Consent withdrawn</h1>
          <p style={c.sub}>
            {withdrawOutcome?.kept
              ? 'Recording stopped. The partial transcript was kept and is saved in Reflections.'
              : 'Recording stopped and everything captured in this session was deleted.'}
          </p>
          <div style={c.controls}>
            <button style={c.primary} onClick={() => onExit?.(withdrawOutcome?.kept ? { openHistory: true } : undefined)}>Done</button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div style={c.card}>
          <p style={c.doneMark}>✓</p>
          <h1 style={c.title}>Session captured</h1>
          {result?.reflection && <p style={c.reflection}>{result.reflection}</p>}
          {result?.resources && (
            <div style={c.resources}>
              {['primary','text','youth','suicide','emergency'].map(k => result.resources[k] && (
                <div key={k} style={c.resource}>{result.resources[k]}</div>
              ))}
            </div>
          )}
          <p style={c.smallNote}>Saved to your Reflections with the full transcript — open it any time to keep talking it through.</p>
          <div style={c.controls}>
            <button style={c.ghost} onClick={() => onExit?.()}>Close</button>
            <button style={c.primary} onClick={() => onExit?.({ openHistory: true })}>Open in Reflections →</button>
          </div>
        </div>
      )}
    </div>
  )
}

const c = {
  wrap:      { minHeight:'100vh', background:'linear-gradient(165deg,#161922,#0e1119)', color:'#e8eef4', padding:'1.1rem 1.1rem 3rem', maxWidth:640, margin:'0 auto' },
  card:      { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1.3rem', marginTop:'2.2rem', textAlign:'center' },
  icon:      { fontSize:'2.2rem' },
  title:     { margin:'0.4rem 0 0.5rem', fontSize:'1.4rem', fontWeight:800, color:'#f0f4f8' },
  sub:       { margin:'0 0 0.9rem', fontSize:'0.92rem', lineHeight:1.6, color:'rgba(240,244,248,.65)' },
  factList:  { textAlign:'left', margin:'0 0 0.9rem', paddingLeft:'1.1rem', display:'flex', flexDirection:'column', gap:'0.4rem' },
  fact:      { fontSize:'0.84rem', lineHeight:1.5, color:'rgba(240,244,248,.7)' },
  controls:  { display:'flex', gap:'0.6rem', marginTop:'0.6rem', flexWrap:'wrap', justifyContent:'center' },
  ghost:     { flex:'1 1 auto', background:'transparent', border:'1px solid rgba(255,255,255,.18)', color:'#e8eef4', fontSize:'0.9rem', fontWeight:600, borderRadius:'0.7rem', padding:'0.8rem 1rem', cursor:'pointer' },
  primary:   { flex:'1 1 auto', background:'#8b5cf6', border:'none', color:'#fff', fontSize:'0.9rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.8rem 1rem', cursor:'pointer' },
  liveWrap:  { minHeight:'80vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', gap:'0.35rem' },
  pulseDot:  { width:18, height:18, borderRadius:'50%', background:'#f87171', animation:'sovcap 1.6s ease-in-out infinite', marginBottom:'0.6rem' },
  liveState: { margin:0, fontSize:'0.85rem', letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(240,244,248,.6)', fontWeight:700 },
  timer:     { margin:'0.1rem 0', fontSize:'2.6rem', fontWeight:800, fontVariantNumeric:'tabular-nums', color:'#f0f4f8' },
  who:       { margin:0, fontSize:'0.88rem', color:'rgba(240,244,248,.6)' },
  saved:     { margin:'0.2rem 0 0', fontSize:'0.8rem', color:'rgba(52,211,153,.85)' },
  liveHint:  { margin:'0.7rem 0 0', fontSize:'0.8rem', lineHeight:1.5, color:'rgba(240,244,248,.45)', maxWidth:420 },
  liveControls:{ display:'flex', flexDirection:'column', gap:'0.7rem', marginTop:'1.4rem', width:'100%', maxWidth:340, alignItems:'stretch' },
  stopBig:   { background:'rgba(248,113,113,.14)', border:'1px solid rgba(248,113,113,.5)', color:'#fecaca', fontSize:'1rem', fontWeight:700, borderRadius:'0.8rem', padding:'0.95rem 1rem', cursor:'pointer' },
  withdrawLink:{ background:'transparent', border:'none', color:'rgba(240,244,248,.55)', fontSize:'0.85rem', textDecoration:'underline', cursor:'pointer', padding:'0.4rem' },
  withdrawChoices:{ display:'flex', flexDirection:'column', gap:'0.7rem', marginTop:'0.4rem' },
  smallNote: { margin:'0.8rem 0 0', fontSize:'0.78rem', color:'rgba(240,244,248,.45)', lineHeight:1.5 },
  doneMark:  { fontSize:'2rem', color:'#6ee7b7', margin:'0 0 0.1rem' },
  reflection:{ fontSize:'0.95rem', lineHeight:1.65, color:'#f4f1ff', textAlign:'left', margin:'0 0 0.8rem' },
  resources: { display:'flex', flexDirection:'column', gap:'0.4rem', margin:'0 0 0.8rem' },
  resource:  { background:'rgba(250,204,21,.08)', border:'1px solid rgba(250,204,21,.3)', borderRadius:'0.6rem', color:'#fde68a', fontSize:'0.82rem', lineHeight:1.5, padding:'0.5rem 0.7rem', textAlign:'left' },
  error:     { color:'#ffb4b4', fontSize:'0.84rem', margin:'0.5rem 0' },
}
